import {
  createOperationScopeSnapshot,
  getOperationKey,
  useTeam,
  useQueryClient,
} from '@vrplatform/data';
import { useContext, useEffect, useRef } from 'react';
import { OperationTrackerContext } from './operationTracker.store';
import type { JournalResource } from './journalFreshness.utils';

const observers = new WeakMap<
  object,
  Map<string, { users: number; dispose: () => void }>
>();

// Observe each resource query once. Operation polls are shared by the existing
// tracker; resource reads resume only after its active work is terminal.
export const useJournalFreshness = ({
  resources,
  refetch,
  queryKey,
  enabled = true,
}: {
  resources: readonly JournalResource[];
  queryKey: readonly unknown[];
  refetch: (options: { cancelRefetch: boolean }) => Promise<unknown>;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const queryIdentity = JSON.stringify(queryKey);
  const store = useContext(OperationTrackerContext);
  const [team] = useTeam();
  const requesterTeamId = team.id;
  const dataRegion = team.dataRegion;
  const regionalApiBaseUrl = team.apiBaseUrl;
  const attempts = useRef(0);
  const snapshot = JSON.stringify(
    resources.map(({ journalStatus, operations }) => ({
      journalStatus,
      operations,
    }))
  );

  useEffect(() => {
    const resources: JournalResource[] = JSON.parse(snapshot);
    if (
      !enabled ||
      !resources.length ||
      resources.every((resource) => resource.journalStatus.state === 'current')
    ) {
      attempts.current = 0;
      return;
    }
    if (!requesterTeamId || !dataRegion) return;
    const registry =
      observers.get(queryClient) ??
      new Map<string, { users: number; dispose: () => void }>();
    observers.set(queryClient, registry);
    const identity = JSON.stringify([
      queryIdentity,
      snapshot,
      requesterTeamId,
      dataRegion,
      regionalApiBaseUrl,
    ]);
    const existing = registry.get(identity);
    if (existing) {
      existing.users++;
      return () => {
        if (--existing.users === 0) {
          existing.dispose();
          registry.delete(identity);
        }
      };
    }
    const scope = createOperationScopeSnapshot({
      apiTarget: 'regional',
      requesterTeamId,
      dataRegion,
      regionalApiBaseUrl,
    });
    const operations = [
      ...new Map(
        resources
          .flatMap((resource) => resource.operations.items)
          .filter(
            (operation) => operation.type === 'reservation-journal-refresh'
          )
          .map((operation) => [operation.id, operation])
      ).values(),
    ];
    if (operations.length && !store)
      throw new Error(
        'Journal freshness monitoring requires OperationTrackerProvider'
      );
    for (const operation of operations)
      store?.getState().recoverOperation({ operation, scope });

    let disposed = false;
    let fetching = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const activeKeys = operations
      .filter(
        (operation) =>
          operation.status === 'queued' || operation.status === 'running'
      )
      .map((operation) =>
        getOperationKey({ ...scope, operationId: operation.id })
      );
    const hasActiveWork = () =>
      activeKeys.some((key) => {
        const status = store?.getState().operations[key]?.status;
        return status === 'queued' || status === 'running';
      });
    let waitingForWork = hasActiveWork();
    const check = async () => {
      if (disposed || fetching || hasActiveWork()) return;
      fetching = true;
      try {
        await refetch({ cancelRefetch: false });
      } finally {
        fetching = false;
        schedule();
      }
    };
    const schedule = (immediate = false) => {
      clearTimeout(timer);
      if (disposed || hasActiveWork()) return;
      // Stale resources wait for focus, reconnection, or query invalidation.
      if (
        !immediate &&
        resources.every(
          (resource) => resource.journalStatus.state !== 'recalculating'
        )
      )
        return;
      timer = setTimeout(
        () => {
          void check();
        },
        immediate ? 0 : Math.min(5_000 * 2 ** attempts.current++, 30_000)
      );
    };
    const unsubscribe = store?.subscribe(() => {
      if (waitingForWork && !hasActiveWork()) {
        waitingForWork = false;
        schedule(true);
      }
    });
    const onFocus = () => {
      if (!hasActiveWork()) schedule(true);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    schedule();
    const observer = {
      users: 1,
      dispose: () => {
        disposed = true;
        clearTimeout(timer);
        unsubscribe?.();
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('online', onFocus);
      },
    };
    registry.set(identity, observer);
    return () => {
      if (--observer.users === 0) {
        observer.dispose();
        registry.delete(identity);
      }
    };
  }, [
    snapshot,
    queryIdentity,
    queryClient,
    enabled,
    store,
    requesterTeamId,
    dataRegion,
    regionalApiBaseUrl,
    refetch,
  ]);
};
